<script setup>
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { canonicalUrl, applySeoMeta } from '@/utils/seo';
import { DEV_MODE } from '@/config/dev-mode';
import { getUserLogin } from '@/composables/useAuthStore.js';
import { usePaymentsStore } from '@/store/modules/payments';
import { useMyInfoStore } from '@/store/modules/myinfo';
import { useRnMessage } from '@/composables/useRnMessage';
import { useClientType } from '@/composables/useClientType';
import { getArticles, getMultiArticles } from '@/api/ArticleApi';
import { campaignParticipate, getCampaign } from '@/api/CampainApi';
import { addContentLogAPI } from '@/api/logServiceApi';
import { getUserInfoAPI } from '@/api/myPage';
import { dismissPopupByContext } from '@/api/popupApi';
import { Calendar, Carousel, Watch, Banner, BannerList, DailyBriefing, MainPopupBanner } from '@/components/main';
import iconSpeakerphone from '@manseImages/icon_speakerphone.svg?url';
import iconWatchFill from '@manseImages/icon_watch_fill.svg?url';
import iconDropdown from '@manseImages/icon_dropdown.svg?url';
import SajuClockPopup from '@/components/main/SajuClockPopup.vue';
import QuickMenuGrid from '@/components/main/QuickMenuGrid.vue';
import ComLatest from '@/components/saju/ComLatest.vue';
import AlertComponent from '@/components/common/Alert.vue';
import AlertMainComponent from '@/components/saju/AlertMain.vue';
import AlertMain02Component from '@/components/saju/AlertMain02.vue';
import ConfirmComponent from '@/components/common/Confirm.vue';
import ConfirmDialog from '@/components/common/ConfirmDialog.vue';
import OnBoardingPage from '@/components/common/OnBoardingPage.vue';
import MobileHeader from '@/components/common/MobileHeader.vue';
import SmartwelPointBar from '@/components/mobile/SmartwelPointBar.vue';
import { useCustomScrollLock } from '@/composables/useCustomScrollLock';
import { useBackLayer } from '@/composables/useAppBack';
import {
  getMainPageCache,
  setMainPageCache,
  setMainPageUserId,
  prefetchPopupList,
} from '@/composables/useMainPageCache';

definePage({ meta: { layout: 'main' } });

const emit = defineEmits(['updateTheme', 'updateScroll']);

/** iconTheme 정보 헤더로 전달 */
const currentIconTheme = ref('WHITE');
const carouselTitle = ref('');
const isResponsiveMain = ref(typeof window !== 'undefined' && window.innerWidth <= 768);
const isFloatButtonUp = ref(false);
const isMainMobileHeaderTransparent = ref(false);
const slideOverlayOpacity = ref(1);
const mobileTitleOpacity = ref(0);
const mobileTitleIntroFactor = ref(1);
const mobileOverlayRangeTop = ref(0);
const mobileOverlayRangeBottom = ref(0);
const hasMobileOverlayRange = ref(false);
const mobileInitialScrollTop = ref(0);
const didSetInitialMobileScroll = ref(false);
const mainRightBoxRef = ref(null);
const isWatchPopupOpen = ref(false);
const isMobileWatchOpen = ref(false);
const sajuClockPopupRef = ref(null);
const toggleMobileWatch = () => {
  isMobileWatchOpen.value = !isMobileWatchOpen.value;
  if (isMobileWatchOpen.value) {
    // 사주시계 팝업이 열릴 때만 로그 저장 (PC watch.vue/watchbg.vue와 동일한 contentType)
    addContentLogAPI('H_saju_watch');
    sajuClockPopupRef.value?.refresh?.();
  }
};

const updateTheme = (newTheme) => {
  currentIconTheme.value = newTheme || 'WHITE';
  emit('updateTheme', currentIconTheme.value);
};
const updateCarouselTitle = (newTitle) => {
  carouselTitle.value = (newTitle || '').trim();
};

const router = useRouter();
const route = useRoute();

applySeoMeta({
  title: '실시간 사주시계와 무료 사주팔자 조회',
  description:
    '실시간 사주시계로 지금의 간지를 확인하고, 매일 새로 쓰는 낭만에세이를 감상하세요. 로그인하면 오늘의 운세, 행운 아이템, 맞춤택일까지 이용 가능합니다.',
  url: canonicalUrl(route.fullPath),
});

const paymentsStore = usePaymentsStore();
const { isApp, serverType } = useClientType();
/** [DEV] 팝업 버튼 노출: DEV_MODE이면서, 앱일 땐 production 서버가 아닐 때만 (운영 앱에선 숨김) */
const showDevPopupBtn = computed(
  () => DEV_MODE && (!isApp.value || serverType.value !== 'production'),
);
const useMobileWeb = ref(localStorage.getItem('nangman_use_mobile_web') === 'true');

const store = useStore();

// 앱(WebView) 전용: 인증 후 유저 정보 + 결제 데이터 로드
const loadAppUserData = async () => {
  // setAuth에서 sessionStorage에 authUserId, X-Token 세팅 완료된 상태
  userId.value = sessionStorage.getItem('authUserId');
  setMainPageUserId(userId.value);

  // store에 유저 정보 세팅
  try {
    const userInfo = await getUserInfoAPI();
    if (userInfo) {
      store.commit('user/SET_NAME', userInfo.name);
      store.commit('user/SET_DATA', userInfo);
      sessionStorage.setItem('userInfo', JSON.stringify(userInfo));
    }
  } catch {
    // 유저 정보 조회 실패 시 무시
  }

  const myInfoStore = useMyInfoStore();
  paymentsStore.getOwnPoint();
  paymentsStore.fetchTicketCount();
  myInfoStore.fetchUnreadAlarm();
  // 파트너(복지찬스) 정보 복원 — 앱에선 Header.vue(fetchMyInfo)가 마운트되지 않아
  // 새로고침 시 헤더 파트너 로고 / SmartwelPointBar가 사라지는 문제 방지
  myInfoStore.fetchMyInfo();
};

/** 로그인/로그아웃 시 모바일 초기 스크롤 위치로 리셋 */
const resetToInitialMobileScroll = async () => {
  await nextTick();
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      didSetInitialMobileScroll.value = false;
      mobileInitialScrollTop.value = 0;
      setInitialMobileScrollPosition();
      updateFloatButtonDirection();
      updateMobileTitleProgress();
    });
  });
};

const { accessToken, setAuth } = useRnMessage({
  onInit: async (payload, action) => {
    if (!payload?.accessToken) return;
    await loadAppUserData();
    isAuthResolved.value = true;
    if (action === 'login') resetToInitialMobileScroll();
  },
  onCustom: async (data) => {
    const { action, payload = {} } = data;
    if (action === 'focusIn') {
      if (payload.accessToken) setAuth(payload);
      if (accessToken.value) await loadAppUserData();
    }
  },
});

// 온보딩에서 메인으로 전환 시 초기 스크롤 위치 재설정
watch(useMobileWeb, async (val) => {
  if (val) {
    await nextTick();
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        didSetInitialMobileScroll.value = false;
        mobileInitialScrollTop.value = 0;
        updateResponsiveState();
        updateMobileOverlayRange();
        setInitialMobileScrollPosition();
        updateFloatButtonDirection();
        updateMobileTitleProgress();
      });
    });
  }
});

const dialogAlert = ref(null);
const dialogAlertMain = ref(null);
const dialogAlertMain02 = ref(null);
const dialogTitle = ref('');
const dialogText = ref('');
const dialogNextUrl = ref('');

const dialogConfrim = ref(null);
const dialogTitle2 = ref('');

/** 새소식 데이터 (모듈 레벨 캐시 활용) */
const _mainCache = getMainPageCache();
const newsData = ref(_mainCache.newsData || []);
const isNewsLoading = ref(!_mainCache.isLoaded);
const currentClockNoticeIndex = ref(0);

const userId = ref(_mainCache.userId || null);
const isAuthResolved = ref(!!_mainCache.userId);
const RESPONSIVE_BREAKPOINT = 768;
// RN 앱이 비동기로 inject하는 값이라 const로 캡처하면 첫 진입 시 0이 박혀 잘못된 헤더 높이/스크롤 위치가 계산된다.
// 사용 시점마다 최신값을 읽도록 getter로 노출한다.
const getSafeAreaTop = () => window.__SAFE_AREA_INSETS__?.top || 0;
const getMobileHeaderHeight = () => 52 + getSafeAreaTop();
const getMobileFixedTitleTop = () => getMobileHeaderHeight() + 8;
const MOBILE_FIXED_TITLE_HIDE_END_GAP = 96;
const MOBILE_FIXED_TITLE_HIDE_DURATION = 120;
const MOBILE_FIXED_TITLE_HIDE_BUFFER = 8;
const MOBILE_FIXED_TITLE_HIDE_SPEED_FACTOR = 0.7;
const MOBILE_INITIAL_TITLE_PROGRESS = 1;
const MOBILE_INITIAL_SCROLL_OFFSET_BASE = 96;
const MOBILE_INITIAL_SCROLL_BASE_VIEWPORT_HEIGHT = 667;
const MOBILE_INITIAL_SCROLL_OFFSET_MAX_SCALE = 1.35;
const MOBILE_INITIAL_SCROLL_MANUAL_GUARD = 8;
const MOBILE_TITLE_INTRO_DURATION = 420;
const MAIN_POPUP_PC_SWITCH_WIDTH = 480;
let mobileTitleIntroRafId = 0;
const responsiveViewportWidth = ref(0);

const isThemeBlack = computed(() => currentIconTheme.value === 'BLACK');
const clockNoticeList = computed(() => (Array.isArray(newsData.value) ? newsData.value : []));
const currentClockNotice = computed(() => {
  const list = clockNoticeList.value;
  if (!list.length) return null;
  const safeIndex = ((currentClockNoticeIndex.value % list.length) + list.length) % list.length;
  return list[safeIndex];
});
const clockNoticeBadges = computed(() => {
  const item = currentClockNotice.value;
  if (!item?.icon) return [];
  return String(item.icon)
    .split(',')
    .map((badge) => badge.trim())
    .filter(Boolean);
});
const usePcPopupLayout = computed(() => {
  return isResponsiveMain.value && responsiveViewportWidth.value > MAIN_POPUP_PC_SWITCH_WIDTH;
});
const popupBannerReady = ref(false);

const setRandomClockNoticeIndex = (list = clockNoticeList.value) => {
  const source = Array.isArray(list) ? list : [];
  if (!source.length) {
    currentClockNoticeIndex.value = 0;
    return;
  }
  currentClockNoticeIndex.value = Math.floor(Math.random() * source.length);
};

const mobileTitleStyle = computed(() => {
  return {
    opacity: isWatchPopupOpen.value
      ? 0
      : Math.min(Math.max(mobileTitleOpacity.value, 0), 1).toFixed(3),
    top: `${getMobileFixedTitleTop()}px`,
  };
});
const clamp01 = (value) => Math.min(Math.max(value, 0), 1);
const smoothstep01 = (value) => {
  const t = clamp01(value);
  return t * t * (3 - 2 * t);
};

const stopMobileTitleIntroAnimation = () => {
  if (typeof window === 'undefined' || !mobileTitleIntroRafId) return;
  window.cancelAnimationFrame(mobileTitleIntroRafId);
  mobileTitleIntroRafId = 0;
};

const startMobileTitleIntroAnimation = () => {
  if (typeof window === 'undefined') return;

  stopMobileTitleIntroAnimation();
  mobileTitleIntroFactor.value = 0;
  const startTime = window.performance.now();

  const animate = (now) => {
    const progress = clamp01((now - startTime) / MOBILE_TITLE_INTRO_DURATION);
    mobileTitleIntroFactor.value = smoothstep01(progress);
    updateMobileTitleProgress();

    if (progress >= 1) {
      mobileTitleIntroFactor.value = 1;
      mobileTitleIntroRafId = 0;
      return;
    }

    mobileTitleIntroRafId = window.requestAnimationFrame(animate);
  };

  mobileTitleIntroRafId = window.requestAnimationFrame(animate);
};

const updateResponsiveState = () => {
  if (typeof window === 'undefined') return;
  responsiveViewportWidth.value = window.innerWidth;
  isResponsiveMain.value = window.innerWidth <= RESPONSIVE_BREAKPOINT;
};

const updateMainMobileHeaderState = () => {
  if (!isResponsiveMain.value || typeof window === 'undefined') {
    isMainMobileHeaderTransparent.value = false;
    return;
  }

  const mainRightTop = mainRightBoxRef.value?.offsetTop || 0;
  if (mainRightTop <= 0) {
    isMainMobileHeaderTransparent.value = true;
    return;
  }

  const transparentEnd = Math.max(mainRightTop - getMobileHeaderHeight(), 0);
  isMainMobileHeaderTransparent.value = window.scrollY < transparentEnd;
};

const updateFloatButtonDirection = () => {
  if (!isResponsiveMain.value || typeof window === 'undefined') {
    isFloatButtonUp.value = false;
    return;
  }

  if (!hasMobileOverlayRange.value) {
    updateMobileOverlayRange();
  }

  if (!hasMobileOverlayRange.value) {
    isFloatButtonUp.value = window.scrollY <= mobileInitialScrollTop.value + 2;
    return;
  }

  // 원래 제목이 헤더에 전혀 가려지지 않는 시점부터 상향 아이콘 표시
  const headerLineAbs = window.scrollY + getMobileHeaderHeight();
  isFloatButtonUp.value = headerLineAbs <= mobileOverlayRangeTop.value + 1;
};

const updateMobileOverlayRange = () => {
  if (!isResponsiveMain.value || typeof window === 'undefined') {
    hasMobileOverlayRange.value = false;
    return;
  }

  // 원래 제목이 헤더에 가려지는 구간만 기준으로 페이드 계산
  const titleEl = document.querySelector('.main-left-box .swiper-slide-active .slide-tit-box');
  if (!titleEl) {
    hasMobileOverlayRange.value = false;
    return;
  }

  const rect = titleEl.getBoundingClientRect();
  const top = rect.top + window.scrollY;
  const bottom = rect.bottom + window.scrollY;

  if (!Number.isFinite(top) || !Number.isFinite(bottom) || bottom <= top) {
    hasMobileOverlayRange.value = false;
    return;
  }

  mobileOverlayRangeTop.value = top;
  mobileOverlayRangeBottom.value = bottom;
  hasMobileOverlayRange.value = true;
};

const getMobileTitleHideWindow = (mainRightTop = mainRightBoxRef.value?.offsetTop || 0) => {
  const hideThreshold = Math.max(mainRightTop - MOBILE_FIXED_TITLE_HIDE_END_GAP, 0);
  const hideStart = Math.max(hideThreshold - MOBILE_FIXED_TITLE_HIDE_DURATION, 0);
  return { hideStart, hideThreshold };
};

const getDynamicInitialScrollOffset = () => {
  if (typeof window === 'undefined') return MOBILE_INITIAL_SCROLL_OFFSET_BASE;

  const ratio = window.innerHeight / MOBILE_INITIAL_SCROLL_BASE_VIEWPORT_HEIGHT;
  const scale = Math.min(Math.max(ratio, 1), MOBILE_INITIAL_SCROLL_OFFSET_MAX_SCALE);
  return MOBILE_INITIAL_SCROLL_OFFSET_BASE * scale;
};

const calculateInitialMobileScrollTop = () => {
  if (!isResponsiveMain.value || typeof window === 'undefined') return 0;

  const mainRightTop = mainRightBoxRef.value?.offsetTop || 0;
  if (mainRightTop <= 0) return 0;

  if (!hasMobileOverlayRange.value) {
    updateMobileOverlayRange();
  }

  if (!hasMobileOverlayRange.value) {
    // Swiper 슬라이드 미렌더링 시 fallback: hideWindow 기반
    // 메인 경로와 동일하게 SAFE_AREA_TOP 보정을 적용해 첫 진입/복귀 간 차이가 생기지 않게 한다
    const { hideStart } = getMobileTitleHideWindow(mainRightTop);
    const fallback = Math.max(hideStart - MOBILE_FIXED_TITLE_HIDE_BUFFER, 0);
    return Math.max(fallback - getSafeAreaTop(), 0);
  }

  const rangeHeight = Math.max(mobileOverlayRangeBottom.value - mobileOverlayRangeTop.value, 1);
  const targetByTitle =
    mobileOverlayRangeTop.value +
    rangeHeight * MOBILE_INITIAL_TITLE_PROGRESS -
    getMobileHeaderHeight() +
    getDynamicInitialScrollOffset();
  const { hideStart } = getMobileTitleHideWindow(mainRightTop);
  const maxTarget = Math.max(hideStart - MOBILE_FIXED_TITLE_HIDE_BUFFER, 0);
  const targetTop = Math.max(Math.min(targetByTitle, maxTarget), 0);

  if (!Number.isFinite(targetTop) || targetTop < 1) return 0;
  return targetTop - getSafeAreaTop();
};

let initialScrollRecoverUntil = 0;

const setInitialMobileScrollPosition = () => {
  if (!isResponsiveMain.value || typeof window === 'undefined') return;

  const initialTop = calculateInitialMobileScrollTop();
  if (initialTop > 0) {
    mobileInitialScrollTop.value = initialTop;
  }

  // 스크롤이 리셋된 경우 복원 (초기 로드 후 3초 이내만)
  if (didSetInitialMobileScroll.value) {
    if (
      initialScrollRecoverUntil > Date.now() &&
      window.scrollY < 1 &&
      mobileInitialScrollTop.value > 0
    ) {
      window.scrollTo({ top: mobileInitialScrollTop.value, behavior: 'auto' });
    }
    return;
  }

  if (window.scrollY > MOBILE_INITIAL_SCROLL_MANUAL_GUARD) {
    didSetInitialMobileScroll.value = true;
    return;
  }

  if (initialTop < 1) return;

  didSetInitialMobileScroll.value = true;
  initialScrollRecoverUntil = Date.now() + 1000;
  window.scrollTo({ top: initialTop, behavior: 'auto' });
  startMobileTitleIntroAnimation();
  updateFloatButtonDirection();
  updateMobileTitleProgress();
};

const updateMobileTitleProgress = () => {
  if (!isResponsiveMain.value || typeof window === 'undefined') {
    slideOverlayOpacity.value = 1;
    mobileTitleOpacity.value = 0;
    isMainMobileHeaderTransparent.value = false;
    return;
  }

  updateMainMobileHeaderState();

  const mainRightTop = mainRightBoxRef.value?.offsetTop || 0;
  if (mainRightTop <= 0) {
    slideOverlayOpacity.value = 1;
    mobileTitleOpacity.value = 0;
    return;
  }

  let originalOpacity = 1;
  if (!hasMobileOverlayRange.value) {
    updateMobileOverlayRange();
  }

  if (hasMobileOverlayRange.value) {
    const headerLineAbs = window.scrollY + getMobileHeaderHeight();
    const rangeHeight = Math.max(mobileOverlayRangeBottom.value - mobileOverlayRangeTop.value, 1);
    const earlyOffset = getSafeAreaTop() + 50;
    const cross = smoothstep01(
      (headerLineAbs - (mobileOverlayRangeTop.value - earlyOffset)) / rangeHeight,
    );
    originalOpacity = 1 - cross;
  }

  originalOpacity = clamp01(originalOpacity);
  slideOverlayOpacity.value = originalOpacity;

  // 헤더 아래 고정 제목 노출/소거
  // 본문 영역 진입 전에 더 빨리 사라지도록 종료 지점을 앞당김
  const { hideStart, hideThreshold } = getMobileTitleHideWindow(mainRightTop);
  const hideRange = Math.max(hideThreshold - hideStart, 1);
  const fastHideRange = Math.max(hideRange * MOBILE_FIXED_TITLE_HIDE_SPEED_FACTOR, 1);
  const hideProgress = smoothstep01((window.scrollY - hideStart) / fastHideRange);
  const hideFade = 1 - hideProgress;
  // 원본 제목이 사라지기 시작하는 순간부터 즉시 교차 페이드
  const appearFade = smoothstep01(1 - originalOpacity);
  mobileTitleOpacity.value = clamp01(appearFade * hideFade * mobileTitleIntroFactor.value);
};

const handleWindowResize = useThrottleFn(() => {
  const wasResponsive = isResponsiveMain.value;
  updateResponsiveState();
  if (!isResponsiveMain.value) {
    didSetInitialMobileScroll.value = false;
    mobileInitialScrollTop.value = 0;
    isFloatButtonUp.value = false;
    stopMobileTitleIntroAnimation();
    mobileTitleIntroFactor.value = 1;
  } else if (!wasResponsive) {
    didSetInitialMobileScroll.value = false;
    mobileInitialScrollTop.value = 0;
    stopMobileTitleIntroAnimation();
    mobileTitleIntroFactor.value = 1;
  }
  updateMobileOverlayRange();
  setInitialMobileScrollPosition();
  updateFloatButtonDirection();
  updateMainMobileHeaderState();
  updateMobileTitleProgress();
}, 100);

const handleWindowScroll = useThrottleFn(() => {
  handleMainRightScroll();
  updateMobileOverlayRange();
  updateFloatButtonDirection();
  updateMainMobileHeaderState();
  updateMobileTitleProgress();
}, 16);

watch(carouselTitle, async () => {
  await nextTick();
  updateMobileOverlayRange();
  setInitialMobileScrollPosition();
  updateFloatButtonDirection();
  updateMainMobileHeaderState();
  updateMobileTitleProgress();
});

watch(
  clockNoticeList,
  (list) => {
    if (!list.length) {
      currentClockNoticeIndex.value = 0;
      return;
    }
    if (currentClockNoticeIndex.value >= list.length) {
      currentClockNoticeIndex.value = 0;
    }
  },
  { immediate: true },
);

const onClickMobileFloatButton = () => {
  if (typeof window === 'undefined') return;

  if (isFloatButtonUp.value) {
    const targetTop =
      mobileInitialScrollTop.value > 0
        ? mobileInitialScrollTop.value
        : calculateInitialMobileScrollTop();
    window.scrollTo({
      top: Math.max(targetTop, 0),
      behavior: 'smooth',
    });
    return;
  }

  window.scrollTo({
    top: 0,
    behavior: 'smooth',
  });
};

const goToNewsArticle = ({ boardTitle, articleId }) => {
  let boardName = '';
  switch (boardTitle) {
    case '공지사항':
    case '만세력_공지사항':
      boardName = 'notice';
      addContentLogAPI('H_news_notice');
      break;
    case '이벤트':
    case '만세력_이벤트':
      boardName = 'event';
      addContentLogAPI('H_news_event');
      break;
    case '사주활용법':
    case '만세력_사주활용법':
      boardName = 'word';
      addContentLogAPI('H_news_guide');
      break;
    default:
      boardName = 'notice';
      addContentLogAPI('H_news_notice');
      break;
  }

  if (articleId) router.push(`/nangman-center/${boardName}/${articleId}`);
  else router.push(`/nangman-center/${boardName}`);
};

const onClickClockNotice = () => {
  const item = currentClockNotice.value;
  if (!item) return;
  goToNewsArticle({
    boardTitle: item?.board?.boardTtl || item?.boardTtl || item?.boardTitle || '공지사항',
    articleId: item?.articleId,
  });
};

const handleMainRightScroll = () => {
  const scrollTop = window.scrollY || document.documentElement.scrollTop;
  emit('updateScroll', scrollTop > 64);
};

const showPopupEvent = ref(false);
const isCheckedHideTodayEvent = ref(false);
const popupEventBannerReady = ref(false);
const eventPopupResolved = ref(false);
const mainPopupBoxReady = ref(false);
const showPopup = ref(false);
const isMainPopupHidden = ref(false);
const isCheckedHideToday = ref(false);
const showPopup2 = ref(false);
const isCheckedHideToday2 = ref(false);
const popupShake = ref(false);

let popupShakeTimer = null;
const triggerPopupShake = () => {
  if (popupShakeTimer) clearTimeout(popupShakeTimer);
  popupShake.value = false;
  requestAnimationFrame(() => {
    popupShake.value = true;
    popupShakeTimer = setTimeout(() => {
      popupShake.value = false;
    }, 420);
  });
};

const handleHideToday = (event) => {
  isCheckedHideToday.value = event.target.checked;
};

const handleHideTodayEvent = (event) => {
  isCheckedHideTodayEvent.value = event.target.checked;
};

const MAIN_POPUP_BOX_DELAY = 150;
const EVENT_POPUP_FALLBACK_MS = 3000;
const MAIN_POPUP_FALLBACK_MS = 5000;
let eventPopupFallbackTimer = null;
let mainPopupFallbackTimer = null;

const clearMainPopupFallback = () => {
  if (mainPopupFallbackTimer) {
    clearTimeout(mainPopupFallbackTimer);
    mainPopupFallbackTimer = null;
  }
};

const revealMainPopup = () => {
  if (!isMainPopupHidden.value) {
    showPopup.value = true;
    // 이미지 로드 실패 등으로 popupBannerReady가 영원히 false면
    // 스크롤 락만 걸린 채 닫기 버튼이 안 보여 먹통이 되므로 안전 타이머로 강제 해제
    clearMainPopupFallback();
    mainPopupFallbackTimer = setTimeout(() => {
      if (!popupBannerReady.value) {
        showPopup.value = false;
      }
    }, MAIN_POPUP_FALLBACK_MS);
  }
};

const clearEventPopupFallback = () => {
  if (eventPopupFallbackTimer) {
    clearTimeout(eventPopupFallbackTimer);
    eventPopupFallbackTimer = null;
  }
};

const handleClosePopupEvent = () => {
  clearEventPopupFallback();
  const days = isCheckedHideTodayEvent.value ? 7 : 1;
  setPopupHideUntil('popupEventLastClosed', days);
  persistPopupDismiss(days, 'main_event');
  eventPopupResolved.value = true;
  // dim이 끊기지 않도록 기본 팝업을 먼저 마운트한 뒤 이벤트 팝업을 내린다
  revealMainPopup();
  showPopupEvent.value = false;
  setTimeout(() => {
    mainPopupBoxReady.value = true;
  }, MAIN_POPUP_BOX_DELAY);
};

const handleEventPopupEmpty = () => {
  clearEventPopupFallback();
  showPopupEvent.value = false;
  eventPopupResolved.value = true;
  mainPopupBoxReady.value = true;
  revealMainPopup();
};

const handleEventPopupLoaded = () => {
  // 이벤트 배너가 정상 노출되면 폴백 타이머 취소 — 사용자가 닫을 때까지 유지
  clearEventPopupFallback();
  popupEventBannerReady.value = true;
};

const handleHideToday2 = (event) => {
  isCheckedHideToday2.value = event.target.checked;
};

const setPopupHideUntil = (key, days) => {
  const hideUntil = Date.now() + days * 24 * 60 * 60 * 1000;
  localStorage.setItem(key, hideUntil.toString());
};

const persistPopupDismiss = (days, pageContext = 'main') => {
  dismissPopupByContext(pageContext, days).catch((error) => {
    DEV_MODE && console.error('Failed to dismiss popup:', error);
  });
};

const resetPopupDismiss = async () => {
  localStorage.removeItem('popupLastClosed');
  localStorage.removeItem('popupEventLastClosed');
  localStorage.removeItem('popup2LastClosed');
  localStorage.removeItem('nmp_popup_analysis_guide_hide_until');
  try {
    await Promise.all([
      dismissPopupByContext('main', 0),
      dismissPopupByContext('main_event', 0),
    ]);
  } catch (error) {
    DEV_MODE && console.error('Failed to reset popup dismiss:', error);
  }
  window.location.reload();
};

const handleEventPopupNavigate = () => {
  clearEventPopupFallback();
  eventPopupResolved.value = true;
  showPopupEvent.value = false;
  mainPopupBoxReady.value = true;
};

const handleMainPopupLoaded = () => {
  clearMainPopupFallback();
  popupBannerReady.value = true;
};

const handleMainPopupEmpty = () => {
  clearMainPopupFallback();
  showPopup.value = false;
};

const handleMainPopupNavigate = () => {
  clearMainPopupFallback();
  showPopup.value = false;
};

const handleClosePopup = () => {
  clearMainPopupFallback();
  const days = isCheckedHideToday.value ? 7 : 1;
  setPopupHideUntil('popupLastClosed', days);
  persistPopupDismiss(days, 'main');
  showPopup.value = false;
};

const handleClosePopup2 = () => {
  const days = isCheckedHideToday2.value ? 7 : 1;
  setPopupHideUntil('popup2LastClosed', days);
  persistPopupDismiss(days, 'main');
  showPopup2.value = false;
};

const handlePopupKeydown = e => {
  if (e.key !== 'Escape') return;
  if (showPopupEvent.value && popupEventBannerReady.value) {
    e.preventDefault();
    handleClosePopupEvent();
  } else if (showPopup.value) {
    e.preventDefault();
    handleClosePopup();
  } else if (showPopup2.value) {
    e.preventDefault();
    handleClosePopup2();
  }
};

onMounted(async () => {
  updateResponsiveState();
  window.addEventListener('resize', handleWindowResize, { passive: true });
  window.addEventListener('scroll', handleWindowScroll, { passive: true });

  // getNoticeList()
  const metaTag = document.querySelector('meta[name="viewport"]');
  if (metaTag) {
    metaTag.setAttribute(
      'content',
      'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no',
    );
  }
  fetchAllMainArticles();

  // 팝업 숨김 기간 체크 (하루/7일)
  const isPopupHidden = key => {
    const hideUntil = localStorage.getItem(key);
    if (!hideUntil) return false;
    const parsed = parseInt(hideUntil, 10);
    if (Number.isNaN(parsed)) {
      localStorage.removeItem(key);
      return false;
    }
    if (Date.now() < parsed) return true;
    localStorage.removeItem(key);
    return false;
  };

  if (isPopupHidden('popupLastClosed')) {
    isMainPopupHidden.value = true;
  }

  // 이벤트/기본 팝업 데이터를 병렬로 prefetch — 표시 순서는 기존 게이팅대로 유지하되
  // 네트워크는 동시에 진행해 기본 팝업 표시 시 cache hit으로 즉시 노출되게 한다.
  if (!isPopupHidden('popupEventLastClosed')) {
    prefetchPopupList('main_event').catch(() => {});
  }
  if (!isMainPopupHidden.value) {
    prefetchPopupList('main').catch(() => {});
  }

  if (isPopupHidden('popupEventLastClosed')) {
    eventPopupResolved.value = true;
    mainPopupBoxReady.value = true;
    revealMainPopup();
  } else {
    showPopupEvent.value = true;
    // 이벤트 팝업 API가 지연/실패해도 기본 팝업으로 폴백되도록 안전 타이머
    eventPopupFallbackTimer = setTimeout(() => {
      if (!eventPopupResolved.value) {
        handleEventPopupEmpty();
      }
    }, EVENT_POPUP_FALLBACK_MS);
  }

  if (isPopupHidden('popup2LastClosed')) {
    showPopup2.value = false;
  }

  // 앱: sessionStorage에 인증 정보가 있으면 즉시 로드, 없으면 onInit 대기
  if (isApp.value) {
    if (sessionStorage.getItem('authUserId')) {
      await loadAppUserData();
    }
    isAuthResolved.value = true;

    await nextTick();
    updateMobileOverlayRange();
    setInitialMobileScrollPosition();
    return;
  }

  userId.value = await getUserLogin();
  setMainPageUserId(userId.value);
  isAuthResolved.value = true;

  if (userId.value) {
    paymentsStore.getOwnPoint();
    paymentsStore.fetchTicketCount();
  } else {
    // 로그인 체크 및 캠페인 참여 가능 여부 확인
    if (userId.value) {
      try {
        const campaignStatus = await getCampaign();
        if (campaignStatus.canParticipate) {
          showPopup2.value = true;
        } else {
          showPopup2.value = false;
        }
      } catch (e) {
        const errorMessage = e.response?.data?.detail || e.detail || '오류가 발생했습니다.';
        showDialogAlert(errorMessage);
        showPopup2.value = false;
      }
    } else {
      showPopup2.value = true;
    }

    //FIX: 개발에서만 적용 25-06-23
    // if (!DEV_MODE) {
    //   showPopup2.value = false
    // }
  }

  await nextTick();
  updateMobileOverlayRange();
  setInitialMobileScrollPosition();
  updateFloatButtonDirection();
  updateMobileTitleProgress();
});

// 앱 로그아웃 시 상태 리셋
const handleAppLogout = () => {
  userId.value = null;
  setMainPageUserId(null);
  resetToInitialMobileScroll();
  // 공지/새소식 영역 무한 스켈레톤 방지 — 로딩 상태로 리셋 후 재조회
  isNewsLoading.value = true;
  fetchAllMainArticles();
};

// 전역 캐시 리셋(로그인/로그아웃) 시 공지 영역 재조회
const handleUserCachesReset = () => {
  isNewsLoading.value = true;
  fetchAllMainArticles();
};

onMounted(() => {
  window.addEventListener('rn:logout', handleAppLogout);
  window.addEventListener('nangman:user-caches-reset', handleUserCachesReset);
  document.addEventListener('keydown', handlePopupKeydown);
});

onUnmounted(() => {
  stopMobileTitleIntroAnimation();
  window.removeEventListener('resize', handleWindowResize);
  window.removeEventListener('scroll', handleWindowScroll);
  window.removeEventListener('rn:logout', handleAppLogout);
  window.removeEventListener('nangman:user-caches-reset', handleUserCachesReset);
  document.removeEventListener('keydown', handlePopupKeydown);
  clearEventPopupFallback();
  clearMainPopupFallback();
});

/** 메인 노출 없을 경우 부족한 만큼 공지사항 요청 */
const getNoticeList = async (size = 3) => {
  try {
    const result = await getArticles({
      boardId: 2325,
      params: {
        page: 1,
        size: 3,
        sort: 'displayDate.desc',
      },
    });
    return result.content || [];
  } catch (e) {
    DEV_MODE && console.log('main getNoticeList error:', e);
    return [];
  }
};

/** 새소식 - 데이터 요청 */
const boardIds = [2520, 2480, 2325];
let fetchArticlesInflight = null;
async function fetchAllMainArticles() {
  // 동시 재요청(로그인/로그아웃 이벤트 중첩 등)으로 인한 무한 루프/중복 호출 방지
  if (fetchArticlesInflight) return fetchArticlesInflight;
  const hasCachedData = Array.isArray(newsData.value) && newsData.value.length > 0;
  if (!hasCachedData) {
    isNewsLoading.value = true;
  }
  const run = async () => {
    try {
      const res = await getMultiArticles({
        params: {
          boardIds,
          page: 1,
          size: 3,
          main: true,
          sort: 'displayDate.desc',
        },
        paramsSerializer: (params) => qs.stringify(params, { arrayFormat: 'repeat' }),
      });
      const articles = res.content || [];

      // TODO: 임시로 프론트에서 필터링, 추후 main=true 시 백엔드에서 필터링
      const filtered = articles.filter((article) => article.isMain === true);

      newsData.value = filtered.slice(0, 3);
      const remain = 3 - newsData.value.length;
      if (remain > 0) {
        const noticeList = await getNoticeList(remain);
        newsData.value = [...newsData.value, ...noticeList.slice(0, remain)];
      }
      setRandomClockNoticeIndex(newsData.value);
      setMainPageCache(newsData.value);
      return newsData.value;
    } catch (e) {
      DEV_MODE && console.log('main fetchAllMainArticles error:', e);
      newsData.value = await getNoticeList(3);
      setRandomClockNoticeIndex(newsData.value);
      setMainPageCache(newsData.value);
      return newsData.value;
    } finally {
      isNewsLoading.value = false;
    }
  };
  fetchArticlesInflight = run();
  try {
    return await fetchArticlesInflight;
  } finally {
    fetchArticlesInflight = null;
  }
}

/** 새소식 - 게시물로 이동 */
const onClickArticle = ({ boardTitle, articleId }) => {
  goToNewsArticle({ boardTitle, articleId });
};

const showDialogAlert = (title, text = '', nextUrl = '') => {
  dialogTitle.value = title;
  dialogText.value = text;
  dialogNextUrl.value = nextUrl;
  dialogAlert.value.openAlertDialog();
};

const printManse = () => {
  showDialogAlert('사주조회 후 이용하여 주시기 바랍니다.', '', '/saju-input');
};

/** 1:1문의 페이지 변경 */
const inquiryMyPage = () => {
  router.push('/mypage/inquiry');
};

/** 1:1문의 로그인 체크 */
const inquiryMyPagego = async () => {
  addContentLogAPI('H_btn_qa');
  const userId = await getUserLogin();

  if (!userId) {
    showDialogConfirm(
      '로그인 화면으로 이동하시겠습니까?',
      '로그인 후 이용 가능합니다.',
      '로그인',
      () =>
        router.push({
          path: '/auth/login',
          query: { to: '/mypage/inquiry' },
        }),
    );
  } else {
    inquiryMyPage();
  }
};

/** 포인트 충전 버튼 클릭시 */
const chargePoints = () => {
  const path = '/purchase/points';
  router.push(path);
};

/** 서비스 구매 버튼 클릭시 */
const goServicePurchase = () => {
  router.push('/purchase/tickets');
};

/** 낭만센터 이벤트 버튼 클릭시 */
const goNangmanCenterEvent = () => {
  addContentLogAPI('H_news_event');
  router.push('/nangman-center/event');
};

/** confirm 다이얼로그 보여주기 */
const showDialogConfirm = (title, title2 = '', text = '', onConfirm = () => {}) => {
  dialogTitle.value = title;
  dialogTitle2.value = title2;
  dialogText.value = text;
  dialogNextUrl.value = onConfirm; // 함수로 전달

  dialogConfrim.value.openConfirmDialog();
};

const handlePointCharge = () => {
  showDialogConfirm(
    '로그인 화면으로 이동하시겠습니까?',
    '로그인 후 이용 가능합니다.',
    '로그인',
    () =>
      router.push({
        path: '/auth/login',
        query: { to: '/purchase/points' },
      }),
  );
};

const showCouponLayer = ref(false);

const cuponDownloadPopup = () => {
  showCouponLayer.value = true;
};

const closeCouponLayer = () => {
  showCouponLayer.value = false;
};

const showCouponFinishLayer = ref(false);
const closeCouponFinishLayer = () => {
  showCouponFinishLayer.value = false;
};

//REVIEW: 캠페인 참여 팝업 확인
const cuponDownloadDo = async () => {
  try {
    const res = await campaignParticipate();
    showCouponLayer.value = false;
    showCouponFinishLayer.value = true;
  } catch (e) {
    const errorMessage = e.response?.data?.detail || e.detail || '오류가 발생했습니다.';
    showDialogAlert(errorMessage);
  }
};

const cuponDownloadLogin = () => {
  showDialogConfirm(
    '로그인 화면으로 이동하시겠습니까?',
    '로그인 후 이용 가능합니다.',
    '로그인',
    () =>
      router.push({
        path: '/auth/login',
        query: { to: '/' },
      }),
  );
};

const couponDownloadGo = () => {
  router.push('/saju-service');
};

// =============== 성향분석/용신분석 퀵메뉴 dialog ===============
const analysisConfirmTitle = ref('');
const analysisConfirmMessage = ref('');
/** 분석 서비스 구매 dialog */
const analysisConfirmDialog = ref(null);
/** 분석권 사용 dialog */
const useTicketConfirmDialog = ref(null);

/** 클릭한 퀵메뉴 - '성향분석' | '용신분석' */
const selectedQuickMenu = ref(null);

/** 분석권 정보 */
const tickets = computed(() => paymentsStore.analysisTicketsData);
/** 성향분석권 보유 여부 */
const hasPersonalityTicket = computed(() =>
  tickets.value?.some((ticket) => ticket.typeId === 3 && ticket.totalCount > 0),
);
/** 용신분석권 보유 여부 */
const hasUsefulGodTicket = computed(() =>
  tickets.value?.some((ticket) => ticket.typeId === 4 && ticket.totalCount > 0),
);

/** 선택한 퀵메뉴 별 분석권 보유 확인 */
const checkAnalysisTicketCount = (menu) => {
  if (menu === '성향분석') {
    return hasPersonalityTicket.value;
  } else if (menu === '용신분석') {
    return hasUsefulGodTicket.value;
  }
  return false;
};

/** 퀵메뉴 클릭 시 - '성향분석' | '용신분석' */
const handleAnalysisService = async (menu) => {
  selectedQuickMenu.value = menu;

  if (!userId.value) {
    // 로그인하지 않은 상태에서 성향분석, 용신분석 메뉴를 클릭했을 때
    showSajuAnalysisConfirm({
      title: '낭만 분석 서비스',
      message:
        '분석권이 있어야 이용할 수 있는 서비스예요.\n안내 및 구매 페이지로 지금 이동하시겠어요?',
    });
  } else {
    if (!checkAnalysisTicketCount(menu)) {
      // 선택한 퀵메뉴의 분석권이 없을 때
      showSajuAnalysisConfirm({
        title: '낭만 분석 서비스',
        message:
          '분석권이 있어야 이용할 수 있는 서비스예요.\n안내 및 구매 페이지로 지금 이동하시겠어요?',
      });
    } else {
      showUseTicketConfirm({
        message: `분석권을 사용하시겠습니까?`,
      });
    }
  }
};

/** 분석 서비스 구매 dialog 핸들러 */
const handleAnalysisConfirm = (action) => {
  if (action === 'confirm') {
    let articleId;
    if (selectedQuickMenu.value === '성향분석') {
      articleId = DEV_MODE ? 7148 : 9204; // 성향분석권
    } else if (selectedQuickMenu.value === '용신분석') {
      articleId = DEV_MODE ? 7166 : 9207; // 용신분석권
    }
    router.push(`/purchase/ticket-detail/2521/${articleId}`);
  } else if (action === 'login') {
    router.push('/auth/login');
  } else if (action === 'goList') {
    router.push('/saju-service');
  }
};

/** 분석 서비스 구매 dialog 열기 */
const showSajuAnalysisConfirm = ({ title, message }) => {
  analysisConfirmTitle.value = title;
  analysisConfirmMessage.value = message;
  analysisConfirmDialog.value.openConfirmDialog();
};

/** 분석권 사용 dialog 열기 */
const showUseTicketConfirm = ({ message }) => {
  analysisConfirmTitle.value = null;
  analysisConfirmMessage.value = message;
  useTicketConfirmDialog.value.openConfirmDialog();
};

const isEventPopupVisible = computed(
  () => showPopupEvent.value && popupEventBannerReady.value,
);
const isPopupVisible = computed(() => showPopup.value);
const isScrollLocked = computed(
  () => isEventPopupVisible.value || isPopupVisible.value || isMobileWatchOpen.value,
);
useCustomScrollLock({ activeScrollLock: isScrollLocked });

useBackLayer({
  id: 'main-event-banner',
  isOpen: isEventPopupVisible,
  closeFromBack: handleClosePopupEvent,
});
useBackLayer({
  id: 'main-banner',
  isOpen: showPopup,
  closeFromBack: handleClosePopup,
});
useBackLayer({
  id: 'main-coupon',
  isOpen: showCouponLayer,
  closeFromBack: closeCouponLayer,
});
useBackLayer({
  id: 'main-coupon-finish',
  isOpen: showCouponFinishLayer,
  closeFromBack: closeCouponFinishLayer,
});
useBackLayer({
  id: 'main-mobile-saju-clock',
  isOpen: isMobileWatchOpen,
  closeFromBack: () => {
    isMobileWatchOpen.value = false;
  },
});
</script>

<template>
  <OnBoardingPage v-if="!useMobileWeb && !isApp" @useMobileWeb="useMobileWeb = true" />
  <div class="main-wrap" v-if="useMobileWeb || !isResponsiveMain || isApp">
    <MobileHeader
      title="낭만만세력"
      :show-back="false"
      :show-menu="true"
      :transparent="isMainMobileHeaderTransparent"
      :show-logo="true"
      :logo-white="isMainMobileHeaderTransparent && !isThemeBlack"
      :hide-title="true"
      :show-partner-logo="true"
    >
      <template #right-prepend>
        <button
          v-if="showDevPopupBtn"
          type="button"
          class="mobile-header__dev-popup-btn"
          @click="resetPopupDismiss"
        >
          팝업
        </button>
      </template>
    </MobileHeader>

    <!-- Event popup (main_event) — 기존 메인 팝업보다 먼저 노출 -->
    <Transition v-if="showPopupEvent" name="popup-slide">
      <div
        v-show="popupEventBannerReady"
        :class="['popup-dim', 'popup-dim--banner', { 'popup-dim--pc-layout': usePcPopupLayout }]"
        @click="triggerPopupShake"
      >
        <div
          :class="[
            'popup-box',
            'st-banner',
            'popup-box--main-banner',
            { 'popup-box--pc-layout': usePcPopupLayout, 'popup-box--shake': popupShake },
          ]"
          @click.stop
        >
          <div class="popup-banner-body">
            <MainPopupBanner
              page-context="main_event"
              :is-mobile-layout="!usePcPopupLayout && isResponsiveMain"
              @loaded="handleEventPopupLoaded"
              @empty="handleEventPopupEmpty"
              @navigate="handleEventPopupNavigate"
            />
          </div>
          <div class="popup-footer">
            <div class="checkbox-wrapper">
              <input
                type="checkbox"
                id="hideTodayEvent"
                class="checkbox"
                v-model="isCheckedHideTodayEvent"
              />
              <label for="hideTodayEvent" class="label btn-today">7일 동안 보지 않기</label>
            </div>
            <button class="btn-close" @click="handleClosePopupEvent">닫기</button>
          </div>
        </div>
      </div>
    </Transition>

    <!-- Add popup box after main-wrap opening -->
    <Transition v-if="showPopup" name="popup-slide">
      <div
        v-show="mainPopupBoxReady && popupBannerReady"
        :class="['popup-dim', 'popup-dim--banner', { 'popup-dim--pc-layout': usePcPopupLayout }]"
        @click="triggerPopupShake"
      >
        <div
          v-show="mainPopupBoxReady && popupBannerReady"
          :class="[
            'popup-box',
            'st-banner',
            'popup-box--main-banner',
            { 'popup-box--pc-layout': usePcPopupLayout, 'popup-box--shake': popupShake },
          ]"
          @click.stop
        >
          <div class="popup-banner-body">
            <MainPopupBanner
              page-context="main"
              :is-mobile-layout="!usePcPopupLayout && isResponsiveMain"
              @loaded="handleMainPopupLoaded"
              @empty="handleMainPopupEmpty"
              @navigate="handleMainPopupNavigate"
            />
          </div>
          <div class="popup-footer">
            <div class="checkbox-wrapper">
              <input type="checkbox" id="hideToday" class="checkbox" v-model="isCheckedHideToday" />
              <label for="hideToday" class="label btn-today">7일 동안 보지 않기</label>
            </div>
            <button class="btn-close" @click="handleClosePopup">닫기</button>
          </div>
        </div>
      </div>
    </Transition>

    <!-- Second popup -->
    <!-- <div v-if="showPopup2" class="popup-dim st-bg-trans" @click="handleClosePopup2">
      <div class="popup-box st-banner" @click.stop>
        <div class="popup-image" @click="cuponDownloadPopup">
          <img src="@/assets/images_manse/main/main-popup-250618.png" alt="팝업 이미지" />
        </div>
        <div class="popup-footer">
          <div class="checkbox-wrapper">
            <input type="checkbox" id="hideToday2" class="checkbox" v-model="isCheckedHideToday2" />
            <label for="hideToday2" class="label btn-today">7일 동안 보지 않기</label>
          </div>
          <button class="btn-close" @click="handleClosePopup2">닫기</button>
        </div>
      </div>
    </div> -->

    <!-- Coupon Layer Popup //TODO: 캠페인 참여 팝업 -->
    <div v-if="showCouponLayer" class="popup-dim" @click="closeCouponLayer">
      <div class="popup-box coupon-popup" @click.stop>
        <div class="coupon-popup-content">
          <div class="coupon-popup-icon">
            <img src="@/assets/images_manse/main/icon-gift.png" alt="coupon" />
          </div>
          <div class="coupon-popup-title">
            낭만성향분석 1회 무료 분석권을<br />
            지급해드립니다!
          </div>
          <div class="coupon-popup-desc">
            지급된 분석권은 낭만분석에 저장된 사주 중<br />
            원하는 명식을 선택해 낭만성향분석을<br />
            <span class="highlight-red"
              >1회 무료로 열람하실 수 있으며<br />사주 선택 후 30일간 이용 가능</span
            >합니다.<br />

            <span class="highlight-black">지금 바로 수락 하시겠습니까?</span>
          </div>
        </div>
        <div class="coupon-popup-actions">
          <button class="btn-later" @click="closeCouponLayer">나중에 받기</button>
          <button class="btn-get" @click="cuponDownloadDo" v-if="userId">지금 받기</button>
          <button class="btn-get" @click="cuponDownloadLogin" v-else>지금 받기</button>
        </div>
      </div>
    </div>
    <!-- Coupon Layer Popup //TODO: 캠페인 참여 팝업 완료 -->
    <div v-if="showCouponFinishLayer" class="popup-dim" @click="closeCouponFinishLayer">
      <div class="popup-box coupon-popup" @click.stop>
        <div class="coupon-popup-content">
          <div class="coupon-popup-icon">
            <img src="@/assets/images_manse/main/icon-gift.png" alt="coupon" />
          </div>
          <div class="coupon-popup-title">
            낭만성향분석 1회 무료 분석권이<br />
            지급되었습니다!
          </div>
          <div class="coupon-popup-desc">
            낭만분석 메뉴에서 사용하실 수 있어요.<br />
            나의 성향과 가능성을 이해하는 시간,<br />
            지금 시작해보세요.<br />
            <span class="highlight-red"
              >* 사용기간 : 받은 날로부터 30일<br />
              사용 후, 30일간 열람 가능
            </span>
          </div>
        </div>
        <div class="coupon-popup-actions">
          <button class="btn-later" @click="closeCouponFinishLayer">나중에 이용하기</button>
          <button class="btn-get" @click="couponDownloadGo">낭만분석 바로가기</button>
        </div>
      </div>
    </div>
    <div class="main-inner-base">
      <div class="main-left-box">
        <div
          v-if="isResponsiveMain && carouselTitle"
          class="main-mobile-title"
          :class="{ 'is-black': isThemeBlack, 'is-hidden': mobileTitleOpacity <= 0 }"
          :style="mobileTitleStyle"
        >
          {{ carouselTitle }}
        </div>
        <div class="essay-content">
          <Carousel
            :essay-overlay-opacity="slideOverlayOpacity"
            @update-theme="updateTheme"
            @update-title="updateCarouselTitle"
          />
        </div>
        <button
          v-if="isResponsiveMain"
          type="button"
          class="main-mobile-float-btn"
          :class="{ 'is-up': isFloatButtonUp, 'is-black': isThemeBlack }"
          aria-label="메인 이동 버튼"
          @click="onClickMobileFloatButton"
        />
      </div>

      <div ref="mainRightBoxRef" class="main-right-box">
        <div class="main-content">
          <div class="main-mid-box">
            <button
              v-if="isResponsiveMain"
              type="button"
              class="clock-notice-btn"
              :disabled="!currentClockNotice"
              @click="onClickClockNotice"
            >
              <img
                :src="iconSpeakerphone"
                alt=""
                aria-hidden="true"
                class="clock-notice-btn__icon"
              />
              <span class="clock-notice-btn__text">{{ currentClockNotice?.articleTtl || '' }}</span>
              <div class="clock-notice-btn__badges" v-if="clockNoticeBadges.length">
                <span
                  v-for="(badge, idx) in clockNoticeBadges"
                  :key="`${badge}-${idx}`"
                  class="clock-notice-btn__badge"
                  :class="badge"
                >
                  {{ badge === 'RECO' ? '추천' : badge }}
                </span>
              </div>
            </button>

            <!-- 브리핑 헤더 (오늘의 낭만 브리핑 + 사주시계 버튼) -->
            <div class="mobile-briefing-header">
              <span class="mobile-briefing-header__badge">오늘의 낭만 브리핑</span>
              <button
                type="button"
                class="mobile-briefing-header__watch-btn"
                @click="toggleMobileWatch"
              >
                <img :src="iconWatchFill" alt="" class="mobile-briefing-header__watch-icon" />
                <span class="mobile-briefing-header__watch-text">사주시계</span>
                <img
                  :src="iconDropdown"
                  alt=""
                  class="mobile-briefing-header__dropdown-icon"
                  :class="{ 'is-open': isMobileWatchOpen }"
                />
              </button>
            </div>
          </div>

          <!-- 데일리 브리핑 -->
          <DailyBriefing
            :user-id="userId"
            :news-data="newsData"
            :is-news-loading="isNewsLoading"
            :is-auth-resolved="isAuthResolved"
            :user-info-header-target="''"
            @click-article="onClickArticle"
          />
        </div>

        <div class="quick-menu-box">
          <ComLatest :pr-pgmode="''" :pr-data="{}" @printclick="printManse()" />
          <button
            v-if="showDevPopupBtn"
            type="button"
            class="dev-popup-reset-btn"
            @click="resetPopupDismiss"
          >
            [DEV] 팝업 열기
          </button>
          <button
            v-if="showDevPopupBtn"
            type="button"
            class="dev-popup-reset-btn"
            @click="router.push('/partner/smartwel?preview=done')"
          >
            [DEV] 구독안내
          </button>
        </div>
      </div>
    </div>
    <AlertComponent
      ref="dialogAlert"
      :title="dialogTitle"
      :text="dialogText"
      :nextUrl="dialogNextUrl"
    />
    <AlertMainComponent
      ref="dialogAlertMain"
      :title="dialogTitle"
      :text="dialogText"
      :nextUrl="dialogNextUrl"
    />
    <ConfirmComponent
      ref="dialogConfrim"
      :title="dialogTitle"
      :title2="dialogTitle2"
      :text="dialogText"
      :nextUrl="dialogNextUrl"
    />

    <ConfirmDialog
      ref="useTicketConfirmDialog"
      :hideCloseButton="true"
      :message="analysisConfirmMessage"
      :leftButtonText="'취소'"
      :rightButtonText="'낭만분석 바로가기'"
      @clickLeft="handleAnalysisConfirm('cancel')"
      @clickRight="handleAnalysisConfirm('goList')"
    />
    <ConfirmDialog
      ref="analysisConfirmDialog"
      :hideCloseButton="userId"
      :title="analysisConfirmTitle"
      :message="analysisConfirmMessage"
      :leftButtonText="userId ? '취소' : '로그인'"
      :rightButtonText="'서비스 구매 바로가기'"
      @clickLeft="handleAnalysisConfirm(userId ? 'cancel' : 'login')"
      @clickRight="handleAnalysisConfirm('confirm')"
    />
    <AlertMain02Component ref="dialogAlertMain02" :title="dialogTitle" :nextUrl="dialogNextUrl" />

    <!-- 모바일: 사주시계 팝업 오버레이 -->
    <div
      v-show="isMobileWatchOpen"
      class="mobile-watch-overlay"
      @click.self="isMobileWatchOpen = false"
    >
      <SajuClockPopup ref="sajuClockPopupRef" @close="isMobileWatchOpen = false" />
    </div>

    <!-- smartwel 제휴 회원: 하단탭 위 포인트 정보 바 (컴포넌트 내부에서 조건 체크) -->
    <SmartwelPointBar />
  </div>
</template>

<style lang="scss">
.header-wrap {
  background-color: transparent;
  .inner-base.pc {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
}

.main-wrap {
  .content-holiday {
    top: auto !important;
  }
  .content-season {
    top: auto !important;
  }

  .data-item span {
    cursor: pointer;
  }
}

.main-layout .main-inner-base .main-right-box {
  display: flex;
  align-items: flex-start;
  padding-bottom: 10px;
}

.mobile-header__dev-popup-btn {
  height: 24px;
  padding: 0 8px;
  font-size: 11px;
  font-weight: 600;
  color: #fff;
  background-color: #d32f2f;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  line-height: 1;
}
.mobile-header__dev-popup-btn:hover {
  background-color: #b71c1c;
}

.dev-popup-reset-btn {
  display: block;
  width: 100%;
  margin-top: 12px;
  padding: 8px 12px;
  font-size: 12px;
  font-weight: 600;
  color: #fff;
  background-color: #d32f2f;
  border: none;
  border-radius: 6px;
  cursor: pointer;
}
.dev-popup-reset-btn:hover {
  background-color: #b71c1c;
}

.main-layout .main-inner-base .main-left-box {
  /* sticky는 layout.scss에서 설정 */
}

// PC 브리핑 헤더: 공지 버튼 + 낭만브리핑 배지 + 사주시계 버튼을 세로로 정렬
.main-wrap .main-mid-box {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  justify-content: flex-start;
  gap: 12px;
  width: 100%;
  max-width: 560px;
}

.main-mobile-title,
.main-mobile-float-btn,
.main-mobile-user-info-header-slot {
  display: none;
}

.clock-notice-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  width: 100%;
  min-height: 24px;
  padding: 0;
  border: 0;
  border-radius: 0;
  background: transparent;
  cursor: pointer;
  text-align: left;
}

.clock-notice-skeleton {
  width: 100%;
  min-height: 24px;
  border-radius: 4px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: clockNoticeShimmer 1.5s infinite;
}

@keyframes clockNoticeShimmer {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

.clock-notice-btn__icon {
  width: 16px;
  height: 16px;
  flex-shrink: 0;
}

.clock-notice-btn__text {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: #333;
  font-size: 15px;
  font-weight: 500;
  line-height: 18px;
  letter-spacing: -0.3px;
}

.clock-notice-btn__badges {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
}

.clock-notice-btn__badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 18px;
  padding: 3px 6px;
  border-radius: 4px;
  color: #ffb401;
  background: rgba(255, 180, 1, 0.1);
  font-size: 10px;
  font-weight: 700;
  line-height: 12px;

  &.NEW {
    color: #ffb401;
    background: rgba(255, 180, 1, 0.1);
  }

  &.HOT {
    color: #eb4c10;
    background: rgba(235, 76, 16, 0.1);
  }

  &.RECO {
    color: #a169c8;
    background: rgba(161, 105, 200, 0.1);
  }

  &.BEST,
  &.EVENT {
    color: #ff6b6b;
    background: rgba(255, 107, 107, 0.1);
  }
}

.mobile-briefing-header {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 8px;
  width: 100%;

  &__badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 3px 8px;
    border-radius: 30px;
    background: #f80;
    color: #fff;
    font-size: 14px;
    font-weight: 600;
    line-height: 1.2;
    white-space: nowrap;
  }

  &__watch-btn {
    display: inline-flex;
    align-items: center;
    gap: 3px;
    padding: 3px 8px;
    border: 1px solid #ebebeb;
    border-radius: 30px;
    background: #fff;
    cursor: pointer;
  }

  &__watch-icon {
    width: 16px;
    height: 16px;
    flex-shrink: 0;
  }

  &__watch-text {
    font-size: 14px;
    font-weight: 600;
    color: #575757;
    line-height: 1.2;
    white-space: nowrap;
  }

  &__dropdown-icon {
    width: 16px;
    height: 16px;
    flex-shrink: 0;
    transition: transform 0.2s ease;

    &.is-open {
      transform: rotate(180deg);
    }
  }
}

@media (max-width: 768px) {
  .header-wrap {
    display: none;
  }

  .main-layout,
  .main-wrap {
    overflow: visible !important;
    height: auto !important;
    position: relative;
    -webkit-user-select: none;
    -moz-user-select: none;
    -ms-user-select: none;
    user-select: none;
    -webkit-touch-callout: none;

    * {
      -webkit-touch-callout: none;
      -webkit-user-drag: none;
      user-drag: none;
    }

    img,
    svg {
      -webkit-user-drag: none;
      user-drag: none;
      pointer-events: none;
    }
  }

  .main-layout .main-inner-base {
    flex-direction: column !important;
    justify-content: flex-start !important;
    gap: 12px !important;
    inline-size: 100% !important;
    max-inline-size: 100% !important;
    margin-inline: auto !important;
    align-items: center !important;
  }

  .main-layout .main-inner-base .main-left-box {
    position: relative !important;
    align-self: auto !important;
    inline-size: 100% !important;
    max-inline-size: 100% !important;
    block-size: auto !important;
    height: auto !important;
  }

  .main-layout .main-inner-base .main-right-box {
    inline-size: 100% !important;
    max-inline-size: 500px !important;
    block-size: auto !important;
    margin-inline: auto !important;
  }

  .main-layout .main-inner-base .main-left-box {
    min-block-size: 0 !important;
  }

  .main-layout .main-inner-base .main-left-box .essay-content {
    justify-content: center !important;
  }

  .main-layout .main-right-box {
    padding-block-start: 0 !important;
  }

  .main-layout .main-inner-base .main-right-box {
    display: block !important;
    block-size: auto !important;
    overflow: visible !important;
    padding-bottom: 0 !important;
  }

  .main-content {
    inline-size: 100% !important;
    max-inline-size: 500px !important;
    margin-inline: auto !important;
    padding-inline: 16px !important;
    box-sizing: border-box;
    min-height: 100vh; /* 초기 스크롤 공간 확보 */
  }

  .main-wrap .main-mid-box {
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
  }

  .clock-notice-btn {
    display: flex;
    align-items: center;
    gap: 6px;
    width: 100%;
    min-height: 24px;
    padding: 0;
    border: 0;
    border-radius: 0;
    background: transparent;
    cursor: pointer;
    text-align: left;
  }

  .clock-notice-btn__icon {
    width: 16px;
    height: 16px;
    flex-shrink: 0;
  }

  .clock-notice-btn__text {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    color: #333;
    font-size: 15px;
    font-weight: 500;
    line-height: 18px;
    letter-spacing: -0.3px;
  }

  .clock-notice-btn__badges {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    flex-shrink: 0;
  }

  .clock-notice-btn__badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    height: 18px;
    padding: 3px 6px;
    border-radius: 4px;
    color: #ffb401;
    background: rgba(255, 180, 1, 0.1);
    font-size: 10px;
    font-weight: 700;
    line-height: 12px;
  }

  .clock-notice-btn__badge.NEW {
    color: #ffb401;
    background: rgba(255, 180, 1, 0.1);
  }

  .clock-notice-btn__badge.HOT {
    color: #eb4c10;
    background: rgba(235, 76, 16, 0.1);
  }

  .clock-notice-btn__badge.RECO {
    color: #a169c8;
    background: rgba(161, 105, 200, 0.1);
  }

  .clock-notice-btn__badge.BEST,
  .clock-notice-btn__badge.EVENT {
    color: #ff6b6b;
    background: rgba(255, 107, 107, 0.1);
  }

  .main-wrap .link-box {
    inline-size: 100%;
    margin-inline-start: 0;
  }

  .link-box-mobile {
    display: contents;
  }

  .link-box-pc {
    display: none;
  }

  .main-wrap .link-box {
    display: none;
  }

  .main-mobile-user-info-header-slot {
    display: block;
  }

  .current-box {
    display: none;
  }

  .mobile-briefing-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0;
    width: 100%;
  }

  .mobile-briefing-header__badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 3px 8px;
    border-radius: 30px;
    background: #f80;
    color: #fff;
    font-size: 14px;
    font-weight: 600;
    line-height: 1.2;
    white-space: nowrap;
  }

  .mobile-briefing-header__watch-btn {
    display: inline-flex;
    align-items: center;
    gap: 3px;
    padding: 3px 8px;
    border: 1px solid #ebebeb;
    border-radius: 30px;
    background: #fff;
    cursor: pointer;
  }

  .mobile-briefing-header__watch-icon {
    width: 16px;
    height: 16px;
    flex-shrink: 0;
  }

  .mobile-briefing-header__watch-text {
    font-size: 14px;
    font-weight: 600;
    color: #575757;
    line-height: 1.2;
    white-space: nowrap;
  }

  .mobile-briefing-header__dropdown-icon {
    width: 16px;
    height: 16px;
    flex-shrink: 0;
    transition: transform 0.2s ease;
  }

  .mobile-briefing-header__dropdown-icon.is-open {
    transform: rotate(180deg);
  }

  .main-wrap .main-clock-wrap .clock-title-box .tit {
    block-size: 18px;
    font-size: 15px;
    line-height: 18px;
  }

  .main-wrap .main-clock-wrap .tit-txt {
    gap: 6px;
    margin-block-end: 12px;
  }

  .main-wrap .i-clock {
    block-size: 18px;
    inline-size: 18px;
    background-size: 18px 18px;
  }

  .main-wrap .data-clock-box .data-val {
    block-size: 38px;
    font-size: 22px;
    inline-size: 38px;
    margin-inline-start: 6px;
  }

  .main-wrap .clock-ganji-box .year-item-box span {
    margin-inline-start: 6px;
  }

  .main-layout .main-right-box .quick-menu-box {
    display: none;
  }

  .main-mobile-title {
    display: flex;
    justify-content: center;
    position: fixed;
    inset-inline-start: 0;
    inset-inline-end: 0;
    inset-block-start: 0;
    z-index: 9;
    padding-inline: 5px;
    text-align: center;
    color: #fff;
    font-family: 'Nanum Myeongjo', serif;
    font-size: 22px;
    font-weight: 400;
    line-height: 1.45;

    text-shadow: 0 1px 4px rgba(0, 0, 0, 0.32);
    pointer-events: none;
    opacity: 0;
    transition: none;
  }

  .main-mobile-title.is-black {
    color: #333;
    text-shadow: none;
  }

  .main-mobile-title.is-hidden {
    visibility: hidden;
  }

  .main-mobile-float-btn {
    display: inline-flex;
    position: absolute;
    inset-inline-start: 50%;
    inset-block-end: 18px;
    transform: translateX(-50%);
    inline-size: 40px;
    block-size: 40px;
    border: none;
    background: transparent;
    color: #f1f1f1;
    align-items: center;
    justify-content: center;
    z-index: 10;
    cursor: pointer;
  }

  .main-mobile-float-btn::before {
    content: '';
    inline-size: 11px;
    block-size: 11px;
    border-right: 2px solid currentColor;
    border-bottom: 2px solid currentColor;
    transform: rotate(45deg);
    margin-block-start: -4px;
  }

  .main-mobile-float-btn.is-up::before {
    transform: rotate(-135deg);
    margin-block-start: 4px;
  }

  .main-mobile-float-btn.is-black {
    color: #333;
  }

  // html, body, #app에 overflow-x: hidden 설정 금지
  // - scoped 아닌 스타일이라 SPA 네비게이션 후에도 DOM에 남아
  //   다른 페이지의 position: sticky를 깨뜨림
  .main-wrap {
    overflow-x: hidden;
    width: 100%;
    position: relative;
  }
}

.go-link-box {
  margin: 48px 0 28px 0;
  padding: 0 80px;

  .quick-links {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 20px;
    padding: 0 0px;
    justify-content: center;
    align-items: center;
    align-self: stretch;

    .link-item {
      display: flex;
      flex-direction: column;
      align-items: center;
      cursor: pointer;
      transition: transform 0.2s ease;

      &:hover {
        transform: translateY(-2px);
      }

      img {
        width: 60px;
        height: 60px;
        object-fit: contain;
        margin-bottom: 8px;
      }

      span {
        color: var(--Gray-800, #333);
        font-size: 14px;
        font-weight: 500;
        text-align: center;
      }
    }
  }
}

.debug-info {
  position: fixed;
  top: 0;
  left: 0;
  background: rgba(0, 0, 0, 0.8);
  color: white;
  padding: 5px;
  z-index: 9999;
}

.popup-dim {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
}

.popup-box--shake {
  animation: popup-pulse 0.25s ease-out both;
}

@keyframes popup-pulse {
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.03);
  }
  100% {
    transform: scale(1);
  }
}

@media (prefers-reduced-motion: reduce) {
  .popup-box--shake {
    animation: none;
  }
}

.popup-box.st-banner {
  background: #fff;
  border-radius: 8px;
  padding: 0;
  width: 480px;
  overflow: hidden;
  gap: 0 !important;

  .popup-image {
    width: 480px;
    height: 480px;
    overflow: hidden;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }

  :deep(img),
  .banner-section img,
  .banner-container img {
    border-radius: 0 !important;
  }

  .banner-section {
    margin-bottom: 0;
    width: 480px;

    .banner-container {
      width: 480px;
      height: 480px !important;
      margin: 0;
      border-radius: 0 !important;

      :deep(.v-carousel) {
        height: 480px !important;
      }

      :deep(.v-carousel-item) {
        height: 480px !important;
      }
    }
  }

  .popup-footer {
    padding: 14px 20px;
    width: 100%;
    display: flex;
    justify-content: space-between;
    align-items: center;
    background: #fff;

    .checkbox-wrapper {
      display: flex;
      align-items: center;
      gap: 8px;
    }

    .btn-today {
      color: var(--Gray-800, #333);
      font-size: 15px;
      font-weight: 500;
      line-height: 24px;
      cursor: pointer;
    }

    .btn-close {
      color: var(--Gray-800, #333);
      text-align: center;
      font-size: 15px;
      font-style: normal;
      font-weight: 700;
      line-height: 24px;
      background: none;
      border: none;
      cursor: pointer;
    }
  }
}

/* 팝업 슬라이드업 애니메이션 (모바일) */
@media (max-width: 768px) {
  .popup-slide-enter-active {
    transition: opacity 0.25s ease;
    .popup-box {
      transition: transform 0.25s ease;
    }
  }
  .popup-slide-enter-from {
    opacity: 0;
    .popup-box {
      transform: translateY(100%);
    }
  }
}

@media (max-width: 768px) {
  .popup-dim--banner:not(.popup-dim--pc-layout) {
    align-items: flex-end;
    justify-content: flex-start;
    background: rgba(0, 0, 0, 0.55);
  }

  .popup-box.st-banner.popup-box--main-banner:not(.popup-box--pc-layout) {
    width: 100%;
    max-width: 100%;
    margin: 0;
    border-radius: 20px 20px 0 0;

    .popup-banner-body {
      width: 100%;
      box-sizing: border-box;
      padding: 0;

      :deep(.banner-section) {
        width: 100% !important;
        max-width: none !important;
        margin: 0 !important;
      }

      :deep(.banner-container) {
        width: 100% !important;
        max-width: none !important;
        height: auto !important;
        aspect-ratio: 1 / 1 !important;
      }

      :deep(.banner-container .swiper),
      :deep(.banner-container .swiper-wrapper),
      :deep(.banner-container .swiper-slide) {
        width: 100% !important;
        height: 100% !important;
      }
    }

    .popup-image {
      width: 100%;
      height: auto;
      aspect-ratio: 1 / 1;
      border-radius: 20px 20px 0 0;
    }

    .banner-section {
      width: 100%;

      .banner-container {
        width: 100%;
        height: auto !important;
        aspect-ratio: 1 / 1;
        border-radius: 16px 16px 0 0 !important;

        :deep(.v-carousel) {
          height: 100% !important;
          border-radius: 16px 16px 0 0 !important;
        }

        :deep(.v-carousel-item) {
          height: 100% !important;
          border-radius: 16px 16px 0 0 !important;
        }
      }
    }

    .popup-footer {
      min-height: 48px;
      padding: 12px 16px calc(12px + var(--safe-area-bottom, 0px));
      box-sizing: border-box;

      .checkbox-wrapper {
        gap: 8px;
        min-width: 0;
      }

      .checkbox {
        display: none;
      }

      .btn-today {
        font-size: 15px;
        line-height: 20px;
      }

      .btn-close {
        padding: 0 8px;
        line-height: 20px;
        flex-shrink: 0;
      }
    }
  }
}

.popup-dim.st-bg-trans {
  background: transparent;
  .popup-image {
    cursor: pointer;
  }
}

.coupon-popup {
  padding: 0;
  width: 325px;
  border-radius: 16px;
  position: relative;
  .coupon-popup-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 32px 24px 76px 24px;
  }
  .coupon-popup-icon {
    margin-bottom: 12px;
  }
  .coupon-popup-title {
    color: var(--Gray-800, #333);
    text-align: center;
    font-size: 15px;
    font-style: normal;
    font-weight: 700;
    line-height: 24px; /* 160% */
  }
  .coupon-popup-desc {
    color: var(--Gray-800, #333);
    text-align: center;
    font-size: 15px;
    font-style: normal;
    font-weight: 400;
    line-height: 24px; /* 160% */
    margin: 4px 0;
    .highlight-red {
      color: var(--orange-300-main, #eb4c10);
      font-weight: 700;
    }
    .highlight-black {
      color: var(--Gray-800, #333);
      text-align: center;
      font-size: 15px;
      font-style: normal;
      font-weight: 700;
      line-height: 24px;
    }
  }
  .coupon-popup-actions {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    display: flex;
    gap: 0;
    width: 100%;
    justify-content: center;
    height: 48px;
    .btn-later {
      flex: 1;
      border-radius: 0px 0px 0px 16px;
      background: var(--Gray-200, #f8f8f8);
      color: #333;
      border: none;
      border-radius: 0 0 0 16px;
      font-size: 15px;
      font-weight: 700;
      cursor: pointer;
      transition: background 0.2s;
      &:hover {
        background: #ececec;
      }
    }
    .btn-get {
      flex: 1;
      border-radius: 0px 0px 16px 0px;
      background: var(--orange-300-main, #eb4c10);
      color: #fff;
      border: none;
      border-radius: 0 0 16px 0;
      font-size: 15px;
      font-weight: 700;
      cursor: pointer;
      transition: background 0.2s;
      &:hover {
        background: #e65c00;
      }
    }
  }
}
.v-dialog-alert {
  z-index: 50000 !important;
}

.mobile-watch-overlay {
  display: flex;
  position: fixed;
  inset: 0;
  z-index: 9999;
  background: rgba(0, 0, 0, 0.4);
  align-items: center;
  justify-content: center;
  padding: 16px;
}
</style>
